import React from 'react';
import { Tooltip } from '@material-ui/core';
import { BOARD_MODES, BOARD_MODE_LABELS } from '../constants';
import { modeBadges } from '../utils';
import { ModeBadge, ModeGroup, ModeOption } from './blocks';

/** The switch's accessible name for the options it shows, for example "Show items, services, or claims". */
const groupLabel = (options) => {
    const names = options.map((option) => BOARD_MODE_LABELS[option].toLowerCase());
    const list =
        names.length > 2 ? `${names.slice(0, -1).join(', ')}, or ${names[names.length - 1]}` : names.join(' or ');
    return `Show ${list}`;
};

/**
 * The Items | Services | Claims switch above the split board. Services shows only when the parent has services, and
 * Claims only when it has open claims. Each option has a count badge (the parent's item lines, services, or open
 * claims) that turns orange while that mode still has something to fix, with a tooltip saying what. Renders nothing
 * when the parent has neither services nor open claims, so the board stays in items mode.
 *
 * @param {{ mode: 'items'|'services'|'claims', onChange: (mode: 'items'|'services'|'claims') => void, layout: import('../utils').SplitLayout, items: Array<{ item_id: string, intangible?: boolean }>, services?: Array<{ service_id: string }>, claims?: Array<{ claim_id: string }> }} props
 *   `mode` is a `BOARD_MODES` value. `items`, `services` and `claims` are the parent's rows from the order read.
 */
export const ModeSwitch = ({ mode, onChange, layout, items, services = [], claims = [] }) => {
    const options = [
        BOARD_MODES.ITEMS,
        ...(services?.length ? [BOARD_MODES.SERVICES] : []),
        ...(claims?.length ? [BOARD_MODES.CLAIMS] : []),
    ];
    if (options.length < 2) return null;
    const badges = modeBadges(layout, items, services, claims);
    return (
        <ModeGroup role="group" aria-label={groupLabel(options)}>
            {options.map((option) => {
                const badge = badges[option];
                const selected = mode === option;
                const button = (
                    <ModeOption
                        type="button"
                        selected={selected}
                        aria-pressed={selected}
                        // The orange badge is colour and a hover tooltip, so say what's left in the name too.
                        aria-label={
                            badge.warn
                                ? `${BOARD_MODE_LABELS[option]}, ${badge.count}: ${badge.tooltip}`
                                : `${BOARD_MODE_LABELS[option]}, ${badge.count}`
                        }
                        onClick={() => onChange(option)}
                    >
                        {BOARD_MODE_LABELS[option]}
                        <ModeBadge
                            warn={badge.warn}
                            selected={selected}
                            data-testid={`split-mode-badge-${option}`}
                            data-warn={badge.warn}
                        >
                            {badge.count}
                        </ModeBadge>
                    </ModeOption>
                );
                // An empty title never opens, so the button keeps its place in the tree as the badge changes.
                return (
                    <Tooltip key={option} title={badge.warn ? badge.tooltip : ''} arrow>
                        {button}
                    </Tooltip>
                );
            })}
        </ModeGroup>
    );
};

export default ModeSwitch;
